import { useState, useEffect } from 'react';
import { DashboardLayout } from '@/components/layouts/DashboardLayout';
import { PageHeader } from '@/components/ui/page-header';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { api } from '@/lib/api';
import { 
  MessageSquare, 
  CheckCircle2, 
  XCircle, 
  Clock, 
  ThumbsUp, 
  ThumbsDown,
  Eye,
  Loader2
} from 'lucide-react';
import { FeedbackDetailsModal } from '@/components/ui/feedback-details-modal';

interface Feedback {
  feedback: {
    id: string;
    rating: 'satisfied' | 'unsatisfied';
    comment: string | null;
    requestRefund: string;
    status: 'pending' | 'reviewed' | 'refunded' | 'rejected';
    createdAt: string;
    adminNotes: string | null;
  };
  generation: {
    id: string;
    productName: string;
    style: string;
    status: string;
  };
  user: {
    id: string;
    email: string;
  };
}

export function AdminFeedbacksPage() {
  const [feedbacks, setFeedbacks] = useState<Feedback[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedFeedback, setSelectedFeedback] = useState<string | null>(null);

  useEffect(() => {
    loadFeedbacks();
  }, []);

  const loadFeedbacks = async () => {
    try {
      const data = await api.get<Feedback[]>('/api/admin/feedbacks');
      setFeedbacks(data);
    } catch (error) {
      console.error('Failed to load feedbacks:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (feedback: Feedback['feedback']) => {
    const variant = 
      feedback.status === 'refunded' ? 'default' : 
      feedback.status === 'rejected' ? 'destructive' : 
      'secondary';

    const icon = 
      feedback.status === 'refunded' ? <CheckCircle2 className="h-3.5 w-3.5" /> :
      feedback.status === 'rejected' ? <XCircle className="h-3.5 w-3.5" /> :
      feedback.requestRefund === 'true' ? <Clock className="h-3.5 w-3.5" /> :
      <MessageSquare className="h-3.5 w-3.5" />;

    const text =
      feedback.status === 'refunded' ? 'Reembolsado' :
      feedback.status === 'rejected' ? 'Rejeitado' :
      feedback.requestRefund === 'true' ? 'Aguardando Análise' :
      'Feedback Enviado';

    return (
      <Badge variant={variant} className="gap-1.5">
        {icon}
        {text}
      </Badge>
    );
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-[400px]">
          <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
      <PageHeader 
        title="Feedbacks de Gerações"
        subtitle="Gerencie feedbacks dos usuários e processe reembolsos"
      />

        {/* Stats */}
        <div className="grid gap-4 md:grid-cols-4">
          <Card>
            <CardHeader className="pb-3">
              <CardDescription>Total de Feedbacks</CardDescription>
              <CardTitle className="text-3xl">{feedbacks.length}</CardTitle>
            </CardHeader>
          </Card>
          <Card>
            <CardHeader className="pb-3">
              <CardDescription>Aguardando Análise</CardDescription>
              <CardTitle className="text-3xl">
                {feedbacks.filter(f => f.feedback.status === 'pending' && f.feedback.requestRefund === 'true').length}
              </CardTitle>
            </CardHeader>
          </Card>
          <Card>
            <CardHeader className="pb-3">
              <CardDescription>Reembolsados</CardDescription>
              <CardTitle className="text-3xl">
                {feedbacks.filter(f => f.feedback.status === 'refunded').length}
              </CardTitle>
            </CardHeader>
          </Card>
          <Card>
            <CardHeader className="pb-3">
              <CardDescription>Taxa de Satisfação</CardDescription>
              <CardTitle className="text-3xl">
                {feedbacks.length > 0 
                  ? Math.round((feedbacks.filter(f => f.feedback.rating === 'satisfied').length / feedbacks.length) * 100)
                  : 0}%
              </CardTitle>
            </CardHeader>
          </Card>
        </div>

        {/* Feedbacks List */}
        <Card>
          <CardHeader>
            <CardTitle>Todos os Feedbacks</CardTitle>
            <CardDescription>
              Clique em um feedback para ver detalhes e processar reembolso
            </CardDescription>
          </CardHeader>
          <CardContent>
            {feedbacks.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                <MessageSquare className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>Nenhum feedback recebido ainda</p>
              </div>
            ) : (
              <div className="space-y-3">
                {feedbacks.map((item) => (
                  <div
                    key={item.feedback.id}
                    className="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    <div className="flex-1 space-y-2">
                      <div className="flex items-center gap-3">
                        {item.feedback.rating === 'satisfied' ? (
                          <ThumbsUp className="h-5 w-5 text-green-600" />
                        ) : (
                          <ThumbsDown className="h-5 w-5 text-red-600" />
                        )}
                        <div>
                          <p className="font-medium">{item.generation.productName}</p>
                          <p className="text-sm text-gray-600">{item.user.email}</p>
                        </div>
                      </div>
                      {item.feedback.comment && (
                        <p className="text-sm text-gray-700 ml-8 italic">
                          "{item.feedback.comment}"
                        </p>
                      )}
                      <div className="flex items-center gap-2 ml-8">
                        {getStatusBadge(item.feedback)}
                        <span className="text-xs text-gray-500">
                          {new Date(item.feedback.createdAt).toLocaleDateString('pt-BR', {
                            day: '2-digit',
                            month: 'short',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit',
                          })}
                        </span>
                      </div>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setSelectedFeedback(item.feedback.id)}
                      className="gap-2"
                    >
                      <Eye className="h-4 w-4" />
                      Ver Detalhes
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Feedback Details Modal */}
      {selectedFeedback && (
        <FeedbackDetailsModal
          feedbackId={selectedFeedback}
          open={!!selectedFeedback}
          onClose={() => setSelectedFeedback(null)}
          onRefundProcessed={loadFeedbacks}
        />
      )}
    </DashboardLayout>
  );
}
